/* eslint-disable no-console */
/* eslint-disable local-rules/no-direct-import */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  MutableRefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import cx from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import MediaQuery from 'react-responsive';
import { withRouter } from 'react-router';

import { selectOrderbookPrice } from 'actions/orderbook';
import { setIsChartScrolling } from 'actions/tradingView';
import Button from 'components/button';
import chartStyles from 'components/chart/index.module.scss';
import { StyledButton, Wrapper } from 'components/confirm';
import Modal from 'components/modal';
import mobileWalletStyles from 'components/primaryHeaderMobile/partials/mobileWalletMenu/index.module.scss';
import { MobileModalTitle } from 'components/primaryHeaderMobile/partials/mobileWalletMenu/mobileAssetStyle';
// import ChartOpenOrderWidget from 'containers/chartOpenOrderWidget';
// import routes from 'constants/routes';
import { calcPrecision, handleInputBlurOnScroll, isFutures, isOptions } from 'helpers';
import { device } from 'helpers/screenSizes';
import Theme from 'helpers/theme';
import { useTheme } from 'hooks';
import useCustomMediaQuery from 'hooks/useMediaQuery';
import { useScrollDetection } from 'hooks/useScrollDetection';
import { userSelector } from 'selectors/otherSelectors';
import { socketActiveSelector } from 'selectors/socketSelectors';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { CancelIcon, CrossModalCloseIcon } from 'styles/assets/icons';

import { widget } from '../../charting_library';
import { STORAGE_KEYS } from '../../constants';
import { CHART_TABS } from './chart.constants';
import { TvStyle } from './chart_style';
import { ChartingLibraryWidgetOptions, IChartingLibraryWidget } from './charting_library';
// import { getInitialVisibleRange } from './helper';
import OpenOrdersWidget from './openOrderWidgets';
import OpenPositionLine from './openPositionLine';
import OpenStopOrderLine from './openStopOrderWidgets';
import { DisplayOrdersSVGIconString, NewTabSvgString } from './tradingSvgIcons';
import chartConfig from './tradingViewChartConfig';

export interface ChartContainerProps {
  symbol: ChartingLibraryWidgetOptions['symbol'];
  interval: ChartingLibraryWidgetOptions['interval'];
  // BEWARE: no trailing slash is expected in feed URL
  datafeed: object;
  library_path: ChartingLibraryWidgetOptions['library_path'];
  charts_storage_url: ChartingLibraryWidgetOptions['charts_storage_url'];
  charts_storage_api_version: ChartingLibraryWidgetOptions['charts_storage_api_version'];
  client_id: ChartingLibraryWidgetOptions['client_id'];
  user_id: ChartingLibraryWidgetOptions['user_id'];
  fullscreen: ChartingLibraryWidgetOptions['fullscreen'];
  autosize: ChartingLibraryWidgetOptions['autosize'];
  studies_overrides: ChartingLibraryWidgetOptions['studies_overrides'];
  container: ChartingLibraryWidgetOptions['container'];
  enabled_features: ChartingLibraryWidgetOptions['enabled_features'];
  disabled_features: ChartingLibraryWidgetOptions['disabled_features'];
  load_last_chart: ChartingLibraryWidgetOptions['load_last_chart'];
  save_load_adapter: ChartingLibraryWidgetOptions['save_load_adapter'];
}

const getTvSymbol = (
  productSymbol: string | undefined,
  currentActiveTab: string,
  spotIndexSymbol?: string | undefined
) => {
  switch (currentActiveTab) {
    case CHART_TABS.MARK_CHART:
      return `MARK:${productSymbol}`;
    case CHART_TABS.INDEX_PRICE_CHART:
      return spotIndexSymbol;
    case CHART_TABS.FUNDING_CHART:
      return `FUNDING:${productSymbol}`;
    case CHART_TABS.PRICE_CHART:
    default:
      return productSymbol;
  }
};

const TradingViewContainer = (props: {
  match: { params: any };
  activeTab: string;
  fullScreen: boolean;
}) => {
  const {
    match: { params },
    activeTab,
    fullScreen,
  } = props;
  const { t } = useTranslation(['trading', 'common']);
  const { isDarkTheme, chartBackgroundColor, activeTheme } = useTheme();
  const theme = Theme(activeTheme);

  const [isChartReady, setChartReady] = useState<boolean>(false);
  const [isOpen, showModal] = useState<boolean>(false);
  const [positionSide, setPositionSide] = useState<string>('');
  const [closeData, setCloseData] = useState<any>({});
  const [showOrders, setShowOrders] = useState<boolean>(true);

  const widgetRef = useRef<null | IChartingLibraryWidget>(null);
  const orderPriceRef = useRef<null | string>(null);
  const updatePriceRef = useRef<null | Function>(null);
  const newTabUrlRef = useRef<null | string>(null);
  const tvWidgetElementRef: MutableRefObject<HTMLDivElement | null> = useRef(null);

  const selectedProduct = useAppSelector(selectedProductSelector);
  const socketActive = useAppSelector(socketActiveSelector);
  const user = useAppSelector(userSelector);

  const { whenAboveMd } = useCustomMediaQuery();

  const appDispatch = useAppDispatch();
  const { attachTo } = useScrollDetection(
    isScrolling => {
      appDispatch(setIsChartScrolling(isScrolling));
    },
    { debounceTime: 500 }
  );

  const isNewTab = window.location.href.indexOf('/app/tradingview') > -1;
  const activeChartTypeInNewTab = params?.chartType;

  const currentActiveTab = isNewTab
    ? activeChartTypeInNewTab || 'price-chart'
    : activeTab || localStorage.getItem(STORAGE_KEYS.TRADINGVIEW_LAST_ACTIVE_CHART_TAB);

  const symbol = useMemo(
    () =>
      getTvSymbol(
        selectedProduct?.symbol,
        currentActiveTab,
        selectedProduct?.spot_index?.symbol
      ),
    [selectedProduct?.symbol, currentActiveTab, selectedProduct?.spot_index?.symbol]
  );

  const initiateCharts = () => {
    if (!socketActive) {
      return;
    }
    const savedChartProperties = localStorage.getItem(
      'tradingview.chartproperties.mainSeriesProperties'
    );

    // console.log('DEBUG', selectedProduct, symbol);
    const widgetOptions = chartConfig({
      symbol,
      interval: JSON.parse(savedChartProperties || '{}').interval || '60',
      themeObj: theme,
      activeTab: currentActiveTab,
      activeTheme: isDarkTheme ? 'dark' : 'light',
      userId: user?.id,
      timeframe: isOptions(selectedProduct?.contract_type)
        ? {
            from: new Date(selectedProduct?.launch_time).getTime() / 1000,
            to: Math.floor(new Date().getTime() / 1000),
          }
        : undefined,
      fullScreen: fullScreen ?? false,
    });

    console.log('DEBUG', theme, widgetOptions);

    // eslint-disable-next-line new-cap
    const tvWidget: IChartingLibraryWidget = new widget(widgetOptions);
    tvWidget?.onChartReady(() => {
      widgetRef.current = tvWidget;
      setChartReady(true);

      // apply iframe event listeners
      const iframe = tvWidgetElementRef.current?.firstElementChild as HTMLIFrameElement;
      if (iframe.contentDocument) {
        let unsubscribeActiveChartEl;
        let unsubscribepriceAxisEl;
        // only for mobile devices
        if (!whenAboveMd) {
          // eslint-disable-next-line no-underscore-dangle
          const activeChartEl = (tvWidget.activeChart() as any)?._chartWidget?._mainDiv;
          // ? mousewheel event of activeChartEl doesnt work on price axis, so adding one on it
          const priceAxisEl = activeChartEl?.querySelector('.price-axis');
          unsubscribeActiveChartEl = attachTo(activeChartEl);
          unsubscribepriceAxisEl = attachTo(priceAxisEl, { eventTypes: ['wheel'] });
        }

        const unsubscribe = handleInputBlurOnScroll(iframe.contentDocument);
        iframe.contentDocument?.addEventListener('beforeunload', () => {
          unsubscribe();
          unsubscribeActiveChartEl?.();
          unsubscribepriceAxisEl?.();
        });
      }

      tvWidget?.activeChart()?.setSymbol(symbol);
      tvWidget
        ?.activeChart()
        ?.getPanes()[0]
        .getMainSourcePriceScale()
        ?.setAutoScale(true);

      tvWidget.applyOverrides({
        // udpates background color of the chart
        'paneProperties.backgroundGradientStartColor': chartBackgroundColor,
        'paneProperties.backgroundGradientEndColor': chartBackgroundColor,
      });

      tvWidget.headerReady().then(() => {
        if (
          user?.id !== '' &&
          (currentActiveTab === CHART_TABS.PRICE_CHART ||
            currentActiveTab === CHART_TABS.MARK_CHART)
        ) {
          const button = tvWidget.createButton();
          button.addEventListener('click', () =>
            setShowOrders(state => {
              return !state;
            })
          );
          button.classList.add('apply-common-tooltip');
          button.setAttribute(
            'data-tooltip',
            t('trading:header.toggleShowHidePositions')
          );
          button.innerHTML = DisplayOrdersSVGIconString();
        }

        if (!isNewTab && whenAboveMd) {
          const newTabButton = tvWidget.createButton();
          newTabButton.addEventListener('click', () =>
            newTabUrlRef.current ? window.open(newTabUrlRef.current) : null
          );
          newTabButton.classList.add('apply-common-tooltip');
          newTabButton.setAttribute('data-tooltip', t('trading:header.openInNewTab'));
          newTabButton.innerHTML = NewTabSvgString();
        }
      });
    });

    // eslint-disable-next-line consistent-return
    return widgetOptions;
  };

  // useEffect to update newTabUrlRef
  useEffect(() => {
    const route = window.location.pathname.replace('/app', '').replace('/trade', '');
    newTabUrlRef.current = `/app/tradingview/${currentActiveTab}${route}`;
  }, [currentActiveTab, selectedProduct?.id]);

  // to initially mount the chart
  useLayoutEffect(() => {
    initiateCharts();
  }, []);

  // change symbol when selected product changes so that switching between charts is smoother
  useEffect(() => {
    if (selectedProduct && socketActive && widgetRef.current && isChartReady) {
      // widgetRef.current?.activeChart()?.resetData();
      widgetRef.current?.activeChart()?.setSymbol(symbol ?? '');

      widgetRef.current
        ?.activeChart()
        ?.getPanes()[0]
        .getMainSourcePriceScale()
        ?.setAutoScale(true);

      if (
        currentActiveTab === CHART_TABS.PRICE_CHART ||
        currentActiveTab === CHART_TABS.MARK_CHART
      ) {
        setShowOrders(true);
      }

      if (
        whenAboveMd &&
        (currentActiveTab === CHART_TABS.PRICE_CHART ||
          (currentActiveTab === CHART_TABS.MARK_CHART &&
            (isFutures(selectedProduct?.contract_type) ||
              isOptions(selectedProduct?.contract_type))))
      ) {
        updatePriceRef.current = () => {
          appDispatch(
            selectOrderbookPrice({
              price: orderPriceRef.current,
              productId: selectedProduct?.id,
              fromTradingView: user?.preferences?.tradeToOrderBookedPriceFill,
            })
          );
        };
      }
    }
  }, [selectedProduct, currentActiveTab, isChartReady]);

  useEffect(() => {
    const tvWidget = widgetRef.current;

    // ! there is no unsubscribe function returned from below subscriptions, so not unsubscribing
    if (isChartReady && tvWidget) {
      tvWidget
        ?.activeChart()
        ?.onIntervalChanged()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ?.subscribe(null, value => {
          // This is causing multiple blob fetch issue
          // tvWidget?.activeChart()?.resetData();
          tvWidget
            ?.activeChart()
            ?.getPanes()[0]
            .getMainSourcePriceScale()
            ?.setAutoScale(true);
        });

      tvWidget?.subscribe('onAutoSaveNeeded', () => {
        tvWidget?.saveChartToServer(
          () => console.log('DEBUG: saveChartToServer'),
          e => console.error('DEBUG: saveChartToServer error', e),
          {
            chartName: symbol,
          }
        );
      });

      if (whenAboveMd) {
        tvWidget
          ?.activeChart()
          .crossHairMoved()
          .subscribe(null, ({ price }: { price: number }) => {
            const precision = calcPrecision(selectedProduct?.tick_size);
            orderPriceRef.current = price.toFixed(precision);
          });

        tvWidget?.subscribe('mouse_up', () => {
          updatePriceRef.current?.();
        });
      }
    }
  }, [isChartReady]);

  const onClose = (dispatchClosePosition, openPosition) => {
    setPositionSide(openPosition?.size > 0 ? 'Long' : 'Short');
    showModal(true);
    setCloseData({
      dispatchClosePosition,
      openPosition,
    });
  };

  const onClickYes = () => {
    closeData.dispatchClosePosition(closeData.openPosition);
    setPositionSide('');
    showModal(false);
    setCloseData({});
  };

  return (
    <>
      <TvStyle
        ref={tvWidgetElementRef}
        id="tv_chart_container"
        className={cx(
          'tv_chart_container',
          // fullScreen && user?.id !== '' ? 'mobile-full-screen w-65vh' : '',
          fullScreen ? 'mobile-full-screen w-100vh' : ''
        )}
      />
      {showOrders && user?.id !== '' && widgetRef?.current && (
        <>
          <OpenStopOrderLine
            widgetRef={widgetRef}
            isNewTab={isNewTab}
            isChartReady={isChartReady}
            currentActiveTab={currentActiveTab}
          />
          <OpenOrdersWidget
            widgetRef={widgetRef}
            isNewTab={isNewTab}
            isChartReady={isChartReady}
            currentActiveTab={currentActiveTab}
          />
          <OpenPositionLine
            widgetRef={widgetRef}
            isNewTab={isNewTab}
            onClose={onClose}
            isChartReady={isChartReady}
            currentActiveTab={currentActiveTab}
          />
        </>
      )}

      <MediaQuery query={device.down.md}>
        <Modal
          isOpen={isOpen}
          onRequestClose={showModal}
          shouldCloseOnOverlayClick
          shouldCloseOnEsc={false}
          className="footer-common-modal"
          classOverlayName="footer-common-modal-overlay"
        >
          <Wrapper>
            <MobileModalTitle className={mobileWalletStyles['modal-title']}>
              <button
                type="button"
                data-testid="close-modal"
                onClick={() => showModal(false)}
              >
                <CancelIcon />
              </button>
            </MobileModalTitle>
            <div className="confirm-text">
              <Trans
                i18nKey="common:close_position"
                values={{
                  product: closeData.openPosition?.product_symbol,
                  side: positionSide,
                }}
              >
                <span className={`side-${positionSide.toLowerCase()}`} />
                <span className="product-name" />
              </Trans>
            </div>
            <div className={chartStyles['yes-button']}>
              <Button
                data-testid="mobile-close-position-yes-button"
                style={{
                  margin: '20px',
                  width: '90%',
                }}
                primary
                onClick={onClickYes}
                className="mobile-close-button"
              >
                {t('common:market_close')}
              </Button>
            </div>
          </Wrapper>
        </Modal>
      </MediaQuery>
      <MediaQuery query={device.up.md}>
        <Modal
          isOpen={isOpen}
          onRequestClose={showModal}
          style={{
            padding: 0,
            zIndex: 99999,
            content: {
              top: '0 !important',
              height: '100%',
              overflow: 'hidden',
            },
          }}
        >
          <Wrapper className="close-position">
            <span
              data-testid="j3avSnRHf2RdfF8vnaI44"
              className="close-icon"
              onClick={() => showModal(false)}
              role="button"
              onKeyPress={() => {}}
              tabIndex={0}
            >
              <CrossModalCloseIcon />
            </span>
            <div className="confirm-text">
              <Trans
                i18nKey="common:close_position"
                values={{
                  product: closeData.openPosition?.product_symbol,
                  side: positionSide,
                }}
              >
                <span className={`side-${positionSide.toLowerCase()}`} />
                <span className="product-name" />
              </Trans>
            </div>
            <div className="buttons">
              <StyledButton
                data-testid="desktop-close-position-yes-button"
                className="confirm-ok-button"
                primary
                onClick={onClickYes}
              >
                {t('common:market_close')}
              </StyledButton>
              <StyledButton
                data-testid="desktop-close-position-cancel-button"
                className="confirm-no-button"
                primary
                onClick={() => showModal(false)}
              >
                {t('common:cancel')}
              </StyledButton>
            </div>
          </Wrapper>
        </Modal>
      </MediaQuery>
    </>
  );
};

export default withRouter(TradingViewContainer);
